import { isChoice } from "./options";
import { type Affine, applyAffine, compose, IDENTITY, rotation, translation } from "./transforms";
import type { BlockDefinition, OptionValue, PointOperation, PointProvenance, PointTuple, ProvenanceValue, ValueReference } from "./types";

const EPSILON = 1e-9;

type Direction = "up" | "down" | "left" | "right";


/** The parts of a value definition the coordinate map reads. */
export interface ValueRange {
  type?: string;
  /** Choice options: the stored value is one of these keys. */
  choices?: Record<string, string>;
  default?: number | string;
  minimum?: number;
  maximum?: number;
}

/** The point-producing subset of a pattern specification. */
export interface CoordinateSchema {
  measurements?: Record<string, ValueRange>;
  options?: Record<string, ValueRange>;
  constants?: Record<string, number>;
  blocks?: Record<string, Partial<Pick<BlockDefinition, "operations" | "derivedValues" | "transforms">>>;
}

export interface CoordinateInputs {
  measurements?: Record<string, number>;
  /** Numbers, or choice keys for choice options. */
  options?: Record<string, OptionValue>;
  constants?: Record<string, number>;
  /** Values that need the geometry runtime, such as edge lengths. */
  derived?: Record<string, number>;
}

interface Point {
  x: number;
  y: number;
}

/** What one operation read while it was evaluated. */
interface Trace {
  points: Set<string>;
  values: Map<string, number>;
  fields: ProvenanceValue[];
}

const NAMESPACES: Record<string, string> = {
  measurement: "measurements",
  option: "options",
  constant: "constants",
  "derived value": "derived",
};

/** Fields read by the supported point operations. Each operation uses a subset. */
interface PointOperationFields {
  id: string;
  operation: string;
  label?: string;
  source?: string;
  x?: ValueReference;
  y?: ValueReference;
  xFrom?: string;
  yFrom?: string;
  from?: string;
  to?: string;
  direction?: string;
  distance?: ValueReference;
  by?: "ratio" | "distance";
  value?: ValueReference;
  between?: [string, string];
  relativeTo?: [string, string];
  axis?: [string, string];
  lengthFrom?: [string, string];
  towardConstructionLine?: { from?: string; direction?: string; offset?: ValueReference };
  around?: string;
  angle?: ValueReference;
  opening?: { point?: string; distance?: ValueReference; direction?: string };
  transform?: string;
}

export class PatternCoordinateError extends Error {
  details: Record<string, unknown>;

  constructor(message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.name = "PatternCoordinateError";
    this.details = details;
  }
}

/** An expression that cannot be parsed, regardless of the values it reads. */
export class ExpressionSyntaxError extends PatternCoordinateError {
  constructor(message: string) {
    super(message);
    this.name = "ExpressionSyntaxError";
  }
}

/**
 * Evaluates the point-producing subset of the v3 pattern schema.
 *
 * Coordinates use the drafting coordinate system: x increases to the right
 * and y increases downwards. The class deliberately does not evaluate curves,
 * edges, pieces, or arbitrary JavaScript.
 */
export class PatternCoordinateMap {
  readonly schema: CoordinateSchema;
  readonly measurements: Record<string, number>;
  /** Numbers, or the selected key for choice options. */
  readonly options: Record<string, OptionValue>;
  readonly constants: Record<string, number>;
  readonly derived: Record<string, number>;
  private points: Record<string, Point> = {};
  private pointsByBlock: Record<string, string[]> = {};
  private provenance: Record<string, PointProvenance> = {};
  private trace: Trace | undefined;
  /** The block whose operations are being evaluated, for transforms. */
  private blockId: string | undefined;

  constructor(schema: CoordinateSchema, inputs: CoordinateInputs = {}) {
    if (!schema || typeof schema !== "object") {
      throw new PatternCoordinateError("A parsed pattern schema is required");
    }

    this.schema = schema;
    this.measurements = resolveDefaults(
      schema.measurements,
      inputs.measurements,
      "measurement",
    );
    this.options = resolveOptions(schema.options, inputs.options);
    this.constants = { ...schema.constants, ...inputs.constants };
    this.derived = { ...inputs.derived };
  }

  /** Generate all point coordinates that can be resolved from the schema. */
  generate(settings: { strict?: boolean } = {}): Record<string, PointTuple> {
    const strict = settings.strict ?? true;
    this.points = {};
    this.pointsByBlock = {};
    this.provenance = {};

    for (const [blockId, block] of Object.entries(this.schema.blocks ?? {})) {
      this.evaluateDerivedValues(blockId, block);
      this.evaluateBlock(blockId, block, strict);
    }

    return this.toArrayMap();
  }

  /** Return one flat map, for example `{ J0: [0, 0], J1: [0, 279] }`. */
  toArrayMap(): Record<string, PointTuple> {
    return Object.fromEntries(
      Object.entries(this.points).map(([id, point]) => [id, [point.x, point.y]]),
    );
  }

  /**
   * Resolve a value reference against the inputs and the points from the last
   * `generate()`. Throws if the reference cannot be resolved.
   */
  resolve(reference: ValueReference): number {
    try {
      return this.value(reference);
    } catch (error) {
      if (error instanceof UnresolvedDependencyError) throw new PatternCoordinateError(error.message);
      throw error;
    }
  }

  /** How each point from the last `generate()` was constructed, keyed by point ID. */
  toProvenance(): Record<string, PointProvenance> {
    return { ...this.provenance };
  }

  /** Return points grouped under their schema block IDs. */
  toBlockMap(): Record<string, Record<string, PointTuple>> {
    return Object.fromEntries(
      Object.entries(this.pointsByBlock).map(([blockId, ids]) => [
        blockId,
        Object.fromEntries(ids.map((id) => [id, [this.points[id].x, this.points[id].y]])),
      ]),
    );
  }

  private evaluateDerivedValues(blockId: string, block: NonNullable<CoordinateSchema["blocks"]>[string]) {
    for (const [id, definition] of Object.entries(block.derivedValues ?? {})) {
      if (this.derived[id] !== undefined) continue;

      // Curve and edge lengths require the geometry runtime. Callers can pass
      // these results through `inputs.derived` until that layer is connected.
      if (definition.operation === "edgeLengthSum" || definition.operation === "pointAlongEdges" || definition.operation === "edgeCrossing") continue;

      throw new PatternCoordinateError(
        `Unsupported derived operation "${definition.operation}" for ${blockId}.${id}`,
        { blockId, derivedId: id, operation: definition.operation },
      );
    }
  }

  private evaluateBlock(blockId: string, block: NonNullable<CoordinateSchema["blocks"]>[string], strict: boolean) {
    this.blockId = blockId;
    const pending: PointOperation[] = [...(block.operations ?? [])];
    const resolvedIds: string[] = [];
    let lastErrors = new Map<string, string>();

    while (pending.length > 0) {
      let progress = false;
      const next: PointOperation[] = [];
      lastErrors = new Map();

      for (const operation of pending) {
        try {
          this.trace = { points: new Set(), values: new Map(), fields: [] };
          const point = this.evaluateOperation(operation as PointOperationFields);
          assertFinitePoint(operation.id, point);
          if (this.points[operation.id]) {
            throw new PatternCoordinateError(`Duplicate point ID "${operation.id}"`);
          }
          this.points[operation.id] = point;
          this.provenance[operation.id] = buildProvenance(blockId, operation as PointOperationFields, this.trace);
          resolvedIds.push(operation.id);
          progress = true;
        } catch (error) {
          if (error instanceof UnresolvedDependencyError) {
            next.push(operation);
            lastErrors.set(operation.id, error.message);
            continue;
          }
          throw wrapOperationError(blockId, operation, error);
        } finally {
          this.trace = undefined;
        }
      }

      if (!progress) {
        if (strict) {
          throw new PatternCoordinateError(
            `Could not resolve ${next.length} operation(s) in block "${blockId}"`,
            {
              blockId,
              unresolved: Object.fromEntries(lastErrors),
            },
          );
        }
        break;
      }

      pending.splice(0, pending.length, ...next);
    }

    this.pointsByBlock[blockId] = resolvedIds;
  }

  private evaluateOperation(operation: PointOperationFields): Point {
    switch (operation.operation) {
      case "absolutePoint":
        return {
          x: this.field("x", operation.x),
          y: this.field("y", operation.y),
        };

      case "directionalOffset": {
        const from = this.point(operation.from);
        const distance = this.field("distance", operation.distance);
        const [dx, dy] = directionVector(operation.direction);
        return { x: from.x + dx * distance, y: from.y + dy * distance };
      }

      case "alignedPoint":
        return {
          x: this.axisValue(operation, "x"),
          y: this.axisValue(operation, "y"),
        };

      case "pointOnLine": {
        const from = this.point(operation.from);
        const to = this.point(operation.to);
        const value = this.field("value", operation.value);
        const ratio = operation.by === "distance"
          ? value / distance(from, to)
          : value;
        return interpolate(from, to, ratio);
      }

      case "midpointByX": {
        const [firstId, secondId] = operation.between ?? [];
        const first = this.point(firstId);
        const second = this.point(secondId);
        return {
          x: (first.x + second.x) / 2,
          y: this.axisValue(operation, "y"),
        };
      }

      case "perpendicularOffset": {
        const from = this.point(operation.from);
        const [lineStartId, lineEndId] = operation.relativeTo ?? [];
        const lineStart = this.point(lineStartId);
        const lineEnd = this.point(lineEndId);
        const length = distance(lineStart, lineEnd);
        if (length < EPSILON) {
          throw new PatternCoordinateError("Cannot offset from a zero-length line");
        }
        const amount = this.field("distance", operation.distance);
        const normal = {
          x: -(lineEnd.y - lineStart.y) / length,
          y: (lineEnd.x - lineStart.x) / length,
        };
        return { x: from.x + normal.x * amount, y: from.y + normal.y * amount };
      }

      case "mirrorPoint": {
        const source = this.point(operation.from);
        const [axisStartId, axisEndId] = operation.axis ?? [];
        const axisStart = this.point(axisStartId);
        const axisEnd = this.point(axisEndId);
        const dx = axisEnd.x - axisStart.x;
        const dy = axisEnd.y - axisStart.y;
        const axisLengthSquared = dx ** 2 + dy ** 2;
        if (axisLengthSquared < EPSILON) {
          throw new PatternCoordinateError("Cannot mirror around a zero-length axis");
        }
        const projection = ((source.x - axisStart.x) * dx + (source.y - axisStart.y) * dy) / axisLengthSquared;
        const projected = { x: axisStart.x + projection * dx, y: axisStart.y + projection * dy };
        return { x: projected.x * 2 - source.x, y: projected.y * 2 - source.y };
      }

      case "shoulderEndpoint":
        return this.shoulderEndpoint(operation);

      case "rotatePoint":
        return this.rotatePoint(operation);

      case "transformPoint": {
        const [x, y] = applyAffine(this.transform(this.blockId, operation.transform), [this.point(operation.from).x, this.point(operation.from).y]);
        return { x, y };
      }

      default:
        throw new PatternCoordinateError(
          `Unsupported point operation "${operation.operation}"`,
        );
    }
  }

  private shoulderEndpoint(operation: PointOperationFields): Point {
    const center = this.point(operation.from);
    const [lengthStartId, lengthEndId] = operation.lengthFrom ?? [];
    const radius = distance(this.point(lengthStartId), this.point(lengthEndId));
    const guide = operation.towardConstructionLine ?? {};
    const guideStart = this.point(guide.from);
    const [dx, dy] = directionVector(guide.direction);
    const offset = guide.offset === undefined ? 0 : this.field("offset", guide.offset);
    const target = { x: guideStart.x + dx * offset, y: guideStart.y + dy * offset };

    // In the v3 pyjama draft the construction line through the guide point is
    // horizontal. Select the circle intersection closest to the guide point.
    const verticalDistance = target.y - center.y;
    const discriminant = radius * radius - verticalDistance * verticalDistance;
    if (discriminant < -EPSILON) {
      throw new PatternCoordinateError("Shoulder circle does not meet its guide line");
    }
    const xOffset = Math.sqrt(Math.max(0, discriminant));
    const candidates = [
      { x: center.x - xOffset, y: target.y },
      { x: center.x + xOffset, y: target.y },
    ];
    return candidates.sort((a, b) => distance(a, target) - distance(b, target))[0];
  }

  /**
   * The move a block's transform makes, read from the points of the last
   * `generate()`. Throws if it cannot be resolved.
   */
  transformMatrix(blockId: string, transformId: string): Affine {
    try {
      return this.transform(blockId, transformId);
    } catch (error) {
      if (error instanceof UnresolvedDependencyError) throw new PatternCoordinateError(error.message);
      throw error;
    }
  }

  private transform(blockId: string | undefined, transformId: string | undefined, seen: string[] = []): Affine {
    const definition = transformId ? this.schema.blocks?.[blockId ?? ""]?.transforms?.[transformId] : undefined;
    if (!definition) throw new PatternCoordinateError(`Unknown transform "${transformId}" in block "${blockId}"`);
    if (seen.includes(transformId!)) throw new PatternCoordinateError(`Transforms follow each other in a loop: ${[...seen, transformId].join(" → ")}`);
    let own: Affine = IDENTITY;
    if ([definition.rotate, definition.translate, definition.align].filter(Boolean).length > 1) throw new PatternCoordinateError(`Transform "${transformId}" needs one of rotate, translate or align`);
    if (definition.rotate) {
      const { around, angle, close, ratio, opening } = definition.rotate;
      const pivot = this.point(around);
      let radians: number;
      if (close) {
        const [moving, target] = close.map((id) => this.point(id));
        let turn = Math.atan2(target.y - pivot.y, target.x - pivot.x) - Math.atan2(moving.y - pivot.y, moving.x - pivot.x);
        if (turn > Math.PI) turn -= 2 * Math.PI;
        if (turn <= -Math.PI) turn += 2 * Math.PI;
        radians = turn * (ratio === undefined ? 1 : this.field("ratio", ratio));
      } else if (opening) {
        const reference = this.point(opening.point);
        const radius = distance(pivot, reference);
        const chord = this.field("opening", opening.distance);
        if (radius < EPSILON || Math.abs(chord) > radius * 2) throw new PatternCoordinateError("Opening is wider than the rotation allows");
        const sign = opening.direction === "anticlockwise" ? -1 : opening.direction === "clockwise" ? 1 : NaN;
        if (Number.isNaN(sign)) throw new PatternCoordinateError(`Unknown rotation direction "${opening.direction}"`);
        radians = sign * 2 * Math.asin(chord / (radius * 2));
      } else if (angle !== undefined) {
        radians = (this.field("angle", angle) * Math.PI) / 180;
      } else {
        throw new PatternCoordinateError(`Transform "${transformId}" needs angle, close or opening`);
      }
      own = rotation([pivot.x, pivot.y], radians);
    } else if (definition.translate) {
      const { dx, dy, along, distance: length } = definition.translate;
      if (along) {
        const [from, to] = along.map((id) => this.point(id));
        const span = distance(from, to);
        if (span < EPSILON) throw new PatternCoordinateError("Cannot shift along a zero-length line");
        const amount = this.field("distance", length);
        own = translation((to.x - from.x) / span * amount, (to.y - from.y) / span * amount);
      } else {
        own = translation(dx === undefined ? 0 : this.field("dx", dx), dy === undefined ? 0 : this.field("dy", dy));
      }
    } else if (definition.align) {
      const [fromStart, fromEnd] = definition.align.from.map((id) => this.point(id));
      const [toStart, toEnd] = definition.align.to.map((id) => this.point(id));
      if (distance(fromStart, fromEnd) < EPSILON || distance(toStart, toEnd) < EPSILON) throw new PatternCoordinateError("Cannot align a zero-length line");
      const turn = Math.atan2(toEnd.y - toStart.y, toEnd.x - toStart.x) - Math.atan2(fromEnd.y - fromStart.y, fromEnd.x - fromStart.x);
      own = compose(translation(toStart.x - fromStart.x, toStart.y - fromStart.y), rotation([fromStart.x, fromStart.y], turn));
    } else {
      throw new PatternCoordinateError(`Transform "${transformId}" needs rotate, translate or align`);
    }
    return definition.follow ? compose(this.transform(blockId, definition.follow, [...seen, transformId!]), own) : own;
  }

  /**
   * Turn `from` about `around`. Angles are in degrees, positive clockwise on the
   * page (y down). `opening` sets the angle that moves `opening.point` by
   * `opening.distance`, as in a slash-and-spread wedge.
   */
  private rotatePoint(operation: PointOperationFields): Point {
    const source = this.point(operation.from);
    const pivot = this.point(operation.around);
    let radians: number;
    if (operation.opening) {
      const reference = this.point(operation.opening.point);
      const radius = distance(pivot, reference);
      const chord = this.field("opening", operation.opening.distance);
      if (radius < EPSILON || chord > radius * 2) {
        throw new PatternCoordinateError("Opening is wider than the rotation allows");
      }
      const sign = operation.opening.direction === "anticlockwise" ? -1 : operation.opening.direction === "clockwise" ? 1 : NaN;
      if (Number.isNaN(sign)) throw new PatternCoordinateError(`Unknown rotation direction "${operation.opening.direction}"`);
      radians = sign * 2 * Math.asin(chord / (radius * 2));
    } else {
      radians = (this.field("angle", operation.angle) * Math.PI) / 180;
    }
    const dx = source.x - pivot.x;
    const dy = source.y - pivot.y;
    return {
      x: pivot.x + dx * Math.cos(radians) - dy * Math.sin(radians),
      y: pivot.y + dx * Math.sin(radians) + dy * Math.cos(radians),
    };
  }

  private axisValue(operation: PointOperationFields, axis: "x" | "y"): number {
    const literal = operation[axis];
    if (literal !== undefined) return this.field(axis, literal);
    const sourceId = axis === "x" ? operation.xFrom : operation.yFrom;
    if (!sourceId) {
      throw new PatternCoordinateError(`Missing ${axis} or ${axis}From`);
    }
    return this.point(sourceId)[axis];
  }

  private point(id: string | undefined): Point {
    if (!id || !this.points[id]) {
      throw new UnresolvedDependencyError(`Point "${id}" is not available`);
    }
    this.trace?.points.add(id);
    return this.points[id];
  }

  /** Resolve one of an operation's value fields and note it for provenance. */
  private field(name: string, input: ValueReference | undefined): number {
    const value = this.value(input);
    if (input !== undefined) this.trace?.fields.push({ field: name, reference: referenceText(input), value });
    return value;
  }

  private value(input: ValueReference | undefined): number {
    if (typeof input === "number") return input;
    if (!input || typeof input !== "object") {
      throw new PatternCoordinateError(`Expected a numeric value reference, received ${String(input)}`);
    }
    if (input.measurement) return this.lookup(this.measurements, input.measurement, "measurement");
    if (input.option) return this.lookup(this.options, input.option, "option");
    if (input.constant) return this.lookup(this.constants, input.constant, "constant");
    if (input.derived) return this.lookup(this.derived, input.derived, "derived value");
    if (input.expression) {
      const context = this.expressionContext();
      return evaluateExpression(input.expression, (path) => {
        const value = resolvePath(context, path);
        const [namespace, id] = path.split(".");
        if (namespace === "points") this.trace?.points.add(id);
        else this.trace?.values.set(path, value);
        return value;
      });
    }
    throw new PatternCoordinateError(`Unknown value reference ${JSON.stringify(input)}`);
  }

  private lookup(source: Record<string, unknown>, id: string, kind: string): number {
    const value = source[id];
    if (value === undefined) {
      throw new UnresolvedDependencyError(`Unknown ${kind} "${id}"`);
    }
    if (typeof value !== "number" || !Number.isFinite(value)) {
      throw new PatternCoordinateError(`${kind} "${id}" is not a finite number`);
    }
    this.trace?.values.set(`${NAMESPACES[kind]}.${id}`, value);
    return value;
  }

  private expressionContext(): ExpressionContext {
    // A choice reads as `options.<id>.<key>`: 1 for the selected key, else 0.
    const options = Object.fromEntries(Object.entries(this.options).map(([id, value]) => {
      const definition = this.schema.options?.[id];
      if (!isChoice(definition)) return [id, value];
      return [id, Object.fromEntries(Object.keys(definition.choices ?? {}).map((key) => [key, key === value ? 1 : 0]))];
    }));
    return {
      measurements: this.measurements,
      options,
      constants: this.constants,
      derived: this.derived,
      points: this.points,
    };
  }
}

class UnresolvedDependencyError extends Error {}

/** Write a value reference the way a spec author would read it. */
function referenceText(input: ValueReference): string {
  if (typeof input === "number") return String(input);
  if (input.measurement) return `measurements.${input.measurement}`;
  if (input.option) return `options.${input.option}`;
  if (input.constant) return `constants.${input.constant}`;
  if (input.derived) return `derived.${input.derived}`;
  return input.expression ?? "";
}

function buildProvenance(blockId: string, operation: PointOperationFields, trace: Trace): PointProvenance {
  return {
    id: operation.id,
    blockId,
    operation: operation.operation,
    label: typeof operation.label === "string" ? operation.label : undefined,
    source: typeof operation.source === "string" ? operation.source : undefined,
    construction: describeConstruction(operation),
    fields: trace.fields,
    dependsOnPoints: [...trace.points],
    dependsOnValues: Object.fromEntries(trace.values),
  };
}

/** A one-line, human-readable account of how an operation places its point. */
function describeConstruction(operation: PointOperationFields): string {
  const pair = (ids: [string, string] | undefined) => (ids ?? []).join("–");
  switch (operation.operation) {
    case "absolutePoint":
      return "Fixed position";
    case "directionalOffset":
      return `From ${operation.from}, ${operation.direction} by distance`;
    case "alignedPoint":
      return `x ${operation.x !== undefined ? "from value" : `from ${operation.xFrom}`}, y ${operation.y !== undefined ? "from value" : `from ${operation.yFrom}`}`;
    case "pointOnLine":
      return operation.by === "distance"
        ? `Along ${operation.from}–${operation.to} by distance`
        : `Along ${operation.from}–${operation.to} by ratio`;
    case "midpointByX":
      return `Midway in x between ${pair(operation.between)}`;
    case "perpendicularOffset":
      return `From ${operation.from}, square off ${pair(operation.relativeTo)} by distance`;
    case "mirrorPoint":
      return `${operation.from} mirrored across ${pair(operation.axis)}`;
    case "shoulderEndpoint":
      return `Length ${pair(operation.lengthFrom)} swung from ${operation.from} to the line through ${operation.towardConstructionLine?.from}`;
    case "transformPoint":
      return `${operation.from} moved by ${operation.transform}`;
    case "rotatePoint":
      return operation.opening
        ? `${operation.from} turned ${operation.opening.direction} about ${operation.around} until ${operation.opening.point} opens by distance`
        : `${operation.from} turned about ${operation.around} by angle`;
    default:
      return operation.operation;
  }
}

type ExpressionContext = Record<string, Record<string, unknown>>;

function resolveDefaults(
  definitions: Record<string, ValueRange> = {},
  supplied: Record<string, number> = {},
  kind: string,
): Record<string, number> {
  const result: Record<string, number> = {};
  for (const [id, definition] of Object.entries(definitions ?? {})) {
    const value = supplied?.[id] ?? definition.default;
    if (value === undefined) {
      throw new PatternCoordinateError(`Missing ${kind} "${id}"`);
    }
    if (typeof value !== "number" || !Number.isFinite(value)) {
      throw new PatternCoordinateError(`${kind} "${id}" must be a finite number`);
    }
    if (definition.minimum !== undefined && value < definition.minimum) {
      throw new PatternCoordinateError(`${kind} "${id}" is below its minimum`);
    }
    if (definition.maximum !== undefined && value > definition.maximum) {
      throw new PatternCoordinateError(`${kind} "${id}" is above its maximum`);
    }
    result[id] = value;
  }
  for (const id of Object.keys(supplied ?? {})) {
    if (!Object.hasOwn(definitions, id)) {
      throw new PatternCoordinateError(`Unknown ${kind} "${id}"`);
    }
  }
  return result;
}

/** Options as resolveDefaults does for numbers; a choice holds a valid key, falling back to its default. */
function resolveOptions(definitions: Record<string, ValueRange> = {}, supplied: Record<string, OptionValue> = {}): Record<string, OptionValue> {
  const numeric: Record<string, ValueRange> = {};
  const numericSupplied: Record<string, number> = {};
  const result: Record<string, OptionValue> = {};
  for (const [id, definition] of Object.entries(definitions ?? {})) {
    if (isChoice(definition)) {
      const value = supplied?.[id];
      result[id] = typeof value === "string" && Object.hasOwn(definition.choices ?? {}, value) ? value : definition.default as string;
    } else {
      numeric[id] = definition;
      if (supplied?.[id] !== undefined) numericSupplied[id] = supplied[id] as number;
    }
  }
  for (const id of Object.keys(supplied ?? {})) {
    if (!Object.hasOwn(definitions, id)) throw new PatternCoordinateError(`Unknown option "${id}"`);
  }
  return { ...resolveDefaults(numeric, numericSupplied, "option"), ...result };
}

const DIRECTIONS: Record<Direction, PointTuple> = {
  up: [0, -1],
  down: [0, 1],
  left: [-1, 0],
  right: [1, 0],
};

function directionVector(direction: string | undefined): PointTuple {
  if (!direction || !Object.hasOwn(DIRECTIONS, direction)) {
    throw new PatternCoordinateError(`Unknown direction "${direction}"`);
  }
  return DIRECTIONS[direction as Direction];
}

function distance(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function interpolate(from: Point, to: Point, ratio: number): Point {
  return {
    x: from.x + (to.x - from.x) * ratio,
    y: from.y + (to.y - from.y) * ratio,
  };
}

function assertFinitePoint(id: string, point: Point) {
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) {
    throw new PatternCoordinateError(`Point "${id}" produced non-finite coordinates`);
  }
}

function wrapOperationError(blockId: string, operation: PointOperation, error: unknown): PatternCoordinateError {
  if (error instanceof PatternCoordinateError) {
    error.message = `${blockId}.${operation.id}: ${error.message}`;
    error.details = { blockId, operationId: operation.id, ...error.details };
    return error;
  }
  return new PatternCoordinateError(
    `${blockId}.${operation.id}: ${error instanceof Error ? error.message : String(error)}`,
    { blockId, operationId: operation.id },
  );
}

interface Token {
  type: "number" | "identifier" | "operator";
  value: string;
}

/** The only functions expressions may call. */
const FUNCTIONS: Record<string, (args: number[]) => number> = {
  sqrt: (args) => {
    if (args.length !== 1) throw new ExpressionSyntaxError("sqrt takes one argument");
    if (args[0] < 0) throw new PatternCoordinateError("sqrt requires a non-negative value");
    return Math.sqrt(args[0]);
  },
  abs: (args) => {
    if (args.length !== 1) throw new ExpressionSyntaxError("abs takes one argument");
    return Math.abs(args[0]);
  },
  min: (args) => Math.min(...args),
  max: (args) => Math.max(...args),
  /**
   * A value picked by size band: band(x, v0, e1, v1, e2, v2, …) is v0 below
   * e1, v1 from e1 up to e2, and so on (the edges ascending). For the books'
   * "sizes 80–116cm height plus 1cm, 122–170cm plus 1.5cm" steps.
   */
  band: (args) => {
    if (args.length < 4 || args.length % 2 === 1) throw new ExpressionSyntaxError("band takes x then v0, e1, v1, …: an even count of at least 4");
    const [x, ...steps] = args;
    let result = steps[0];
    for (let index = 1; index < steps.length; index += 2) {
      if (index > 1 && steps[index] <= steps[index - 2]) throw new PatternCoordinateError("band edges must ascend");
      if (x >= steps[index]) result = steps[index + 1];
    }
    return result;
  },
  /** Logic for conditions: 1 when true, 0 when false; non-zero is true. */
  not: (args) => {
    if (args.length !== 1) throw new ExpressionSyntaxError("not takes one argument");
    return args[0] === 0 ? 1 : 0;
  },
  and: (args) => (args.every((value) => value !== 0) ? 1 : 0),
  or: (args) => (args.some((value) => value !== 0) ? 1 : 0),
};

// Small recursive-descent parser for numeric paths, + - * /, parentheses, and
// explicitly allow-listed numeric functions. It has no arbitrary calls,
// assignments, properties outside the supplied context, or JavaScript globals.
function evaluateExpression(source: string, resolve: (path: string) => number): number {
  const tokens = tokenize(source);
  let index = 0;
  // Above zero while parsing arguments an and/or has already settled.
  let skipping = 0;

  function peek(value: string): boolean {
    return tokens[index]?.value === value;
  }

  function consume(value: string): Token {
    if (!peek(value)) throw new ExpressionSyntaxError(`Expected "${value}" in expression`);
    return tokens[index++];
  }

  function primary(): number {
    const token = tokens[index];
    if (!token) throw new ExpressionSyntaxError("Unexpected end of expression");
    if (token.type === "number") {
      index += 1;
      return Number(token.value);
    }
    if (token.type === "identifier") {
      index += 1;
      if (Object.hasOwn(FUNCTIONS, token.value) && peek("(")) {
        consume("(");
        // and/or stop at the first argument that settles them: the rest is
        // parsed but not read, so and(raglan, points.R.y) needs no R when
        // raglan is off.
        const settles = token.value === "and" ? (value: number) => value === 0 : token.value === "or" ? (value: number) => value !== 0 : () => false;
        const skippedBefore = skipping;
        const args = [additive()];
        if (settles(args[0])) skipping += 1;
        while (peek(",")) {
          consume(",");
          args.push(additive());
          if (skipping === skippedBefore && settles(args[args.length - 1])) skipping += 1;
        }
        consume(")");
        skipping = skippedBefore;
        return skipping ? 0 : FUNCTIONS[token.value](args);
      }
      return skipping ? 0 : resolve(token.value);
    }
    if (peek("(")) {
      consume("(");
      const result = additive();
      consume(")");
      return result;
    }
    if (peek("+")) {
      consume("+");
      return primary();
    }
    if (peek("-")) {
      consume("-");
      return -primary();
    }
    throw new ExpressionSyntaxError(`Unexpected token "${token.value}" in expression`);
  }

  function multiplicative(): number {
    let result = primary();
    while (peek("*") || peek("/")) {
      const operator = tokens[index++].value;
      const right = primary();
      result = operator === "*" ? result * right : result / right;
    }
    return result;
  }

  function additive(): number {
    let result = multiplicative();
    while (peek("+") || peek("-")) {
      const operator = tokens[index++].value;
      const right = multiplicative();
      result = operator === "+" ? result + right : result - right;
    }
    return result;
  }

  const result = additive();
  if (index !== tokens.length) {
    throw new ExpressionSyntaxError(`Unexpected token "${tokens[index].value}" in expression`);
  }
  if (!Number.isFinite(result)) {
    throw new PatternCoordinateError(`Expression did not produce a finite number: ${source}`);
  }
  return result;
}

/**
 * List the dotted paths an expression reads, such as `measurements.chest` or
 * `points.B4.x`. Throws `ExpressionSyntaxError` if the expression cannot be parsed.
 */
export function expressionPaths(source: string): string[] {
  const paths: string[] = [];
  try {
    evaluateExpression(source, (path) => {
      paths.push(path);
      return 1;
    });
  } catch (error) {
    // Placeholder values can trip numeric checks (sqrt of a negative, division
    // by zero); only parsing failures matter here.
    if (error instanceof ExpressionSyntaxError) throw error;
  }
  return paths;
}

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let remaining = source.trim();
  while (remaining) {
    const whitespace = remaining.match(/^\s+/);
    if (whitespace) {
      remaining = remaining.slice(whitespace[0].length);
      continue;
    }
    const number = remaining.match(/^(?:\d+(?:\.\d*)?|\.\d+)/);
    if (number) {
      tokens.push({ type: "number", value: number[0] });
      remaining = remaining.slice(number[0].length);
      continue;
    }
    const identifier = remaining.match(/^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*/);
    if (identifier) {
      tokens.push({ type: "identifier", value: identifier[0] });
      remaining = remaining.slice(identifier[0].length);
      continue;
    }
    if ("+-*/(),".includes(remaining[0])) {
      tokens.push({ type: "operator", value: remaining[0] });
      remaining = remaining.slice(1);
      continue;
    }
    throw new ExpressionSyntaxError(`Invalid expression near "${remaining}"`);
  }
  return tokens;
}

function resolvePath(context: ExpressionContext, path: string): number {
  let value: unknown = context;
  for (const part of path.split(".")) {
    if (!value || typeof value !== "object" || !Object.hasOwn(value, part)) {
      throw new UnresolvedDependencyError(`Expression dependency "${path}" is not available`);
    }
    value = (value as Record<string, unknown>)[part];
  }
  if (typeof value !== "number") {
    throw new PatternCoordinateError(`Expression dependency "${path}" is not numeric`);
  }
  return value;
}
